import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from './AuthContext';
import { toast } from 'react-hot-toast';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  model?: string;
  /** Reasoning text parsed out of <think>/<thinking> tags, see splitThinking() */
  thinking?: string;
  /** true while the model is still inside an unclosed <think> tag */
  thinkingOpen?: boolean;
  thinkingMs?: number;
  /** raw `status` events from the backend, e.g. "searching", "planning" */
  statusText?: string;
  /** Real follow-up suggestions parsed from a <suggestions> tag the model
   * appends to its own response - see splitSuggestions(). Not a client-side
   * keyword guess. */
  suggestions?: string[];
  /** If this message came from an Agent Swarm run, the real generated files
   * (path + content), so the chat message can offer a download button
   * instead of only showing the plain-text instructions. */
  swarmFiles?: { path: string; content: string }[];
  /** Real files attached to this message (paperclip icon), persisted server-
   * side in chat_message_attachments - not client-only state. `previewUrl`
   * is a local object-URL for an instant thumbnail right after sending and
   * is absent once the message is hydrated from the server (GET session). */
  attachments?: {
    id: string;
    filename: string;
    mimeType: string;
    size: number;
    status: string;
    previewUrl?: string;
  }[];
}

interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  updatedAt: number;
  pinned?: boolean;
}

interface SendOptions {
  model?: string;
  files?: string[];
}

interface ChatStoreContextType {
  sessions: ChatSession[];
  activeSession: ChatSession | null;
  activeSessionId: string | null;
  isLoaded: boolean;
  isLoading: boolean;
  isStreaming: boolean;
  /** id of the assistant message currently receiving tokens, or null */
  streamingMessageId: string | null;
  createNewChat: () => Promise<string>;
  selectChat: (id: string) => void;
  deleteChat: (id: string) => void;
  sendMessage: (sessionId: string, content: string, options?: SendOptions) => Promise<void>;
  addMessage: (sessionId: string, message: ChatMessage) => void;
  abortGeneration: () => void;
  setSessions: React.Dispatch<React.SetStateAction<ChatSession[]>>;
  setActiveSessionId: React.Dispatch<React.SetStateAction<string | null>>;
  renameChat: (id: string, title: string) => void;
  togglePin: (id: string) => void;
}

const ChatStoreContext = createContext<ChatStoreContextType | null>(null);

/**
 * Splits a raw streamed string into { thinking, content, isThinkingOpen }
 * based on a single <think>...</think> or <thinking>...</thinking> block.
 * Handles the tag being split across chunks since it re-parses the full
 * buffer on every token (cheap for chat-length text).
 */
export function splitThinking(raw: string): { thinking: string; content: string; isThinkingOpen: boolean } {
  const openTag = /<(think|thinking)>/i;
  const openMatch = raw.match(openTag);
  if (!openMatch || openMatch.index === undefined) {
    return { thinking: '', content: raw, isThinkingOpen: false };
  }

  const before = raw.slice(0, openMatch.index);
  const afterOpen = raw.slice(openMatch.index + openMatch[0].length);
  const closeTag = /<\/(think|thinking)>/i;
  const closeMatch = afterOpen.match(closeTag);

  if (!closeMatch || closeMatch.index === undefined) {
    // Still inside the thinking block — nothing to show as content yet
    // beyond whatever came before the tag.
    return { thinking: afterOpen, content: before, isThinkingOpen: true };
  }

  const thinking = afterOpen.slice(0, closeMatch.index);
  const afterClose = afterOpen.slice(closeMatch.index + closeMatch[0].length);
  return { thinking, content: before + afterClose, isThinkingOpen: false };
}

/**
 * Extracts real follow-up suggestions from a <suggestions>[...]</suggestions>
 * block the model appends to the end of its own response (see
 * FOLLOWUP_INSTRUCTION on the backend) - not a client-side keyword guess.
 * While the tag is open but not yet closed (still streaming), strips the
 * partial/invalid JSON from view rather than flashing broken text.
 */
export function splitSuggestions(text: string): { content: string; suggestions: string[] } {
  const openTag = /<suggestions>/i;
  const openMatch = text.match(openTag);
  if (!openMatch || openMatch.index === undefined) {
    return { content: text, suggestions: [] };
  }

  const before = text.slice(0, openMatch.index).trimEnd();
  const afterOpen = text.slice(openMatch.index + openMatch[0].length);
  const closeMatch = afterOpen.match(/<\/suggestions>/i);

  if (!closeMatch || closeMatch.index === undefined) {
    // Still streaming the tag's contents - hide it entirely until closed.
    return { content: before, suggestions: [] };
  }

  const raw = afterOpen.slice(0, closeMatch.index).trim();
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return {
        content: before,
        suggestions: parsed.filter((s): s is string => typeof s === 'string' && s.trim().length > 0).slice(0, 3),
      };
    }
  } catch {
    // Model didn't produce valid JSON - drop it silently rather than show
    // a broken chip or leak raw JSON into the message body.
  }
  return { content: before, suggestions: [] };
}

const ls = {
  get: (key: string, fallback?: any) => {
    try {
      const v = localStorage.getItem(key);
      return v ? JSON.parse(v) : fallback;
    } catch {
      return fallback;
    }
  },
};

export const ChatStoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const { isAuthenticated, user } = useAuth();
  const abortControllerRef = useRef<AbortController | null>(null);
  // Mirrors isStreaming for the session-hydration effect below to read at
  // the moment its fetch resolves, not just when the fetch started - a
  // plain isStreaming read in a stale closure raced streaming that began
  // *during* the fetch (send a message right after opening a session) and
  // let an empty/stale server response clobber the message that was just
  // added locally.
  const isStreamingRef = useRef(isStreaming);
  isStreamingRef.current = isStreaming;

  const activeSession = sessions.find((s) => s.id === activeSessionId) || null;

  // Load from localStorage, scoped per-user - a shared, non-user-scoped
  // key here previously caused one account to see another account's
  // cached session list after switching users on the same browser (real
  // cross-user leak, confirmed 2026-09-01). Depends on user?.id so it also
  // reloads correctly if the user changes without a full page reload.
  useEffect(() => {
    if (!user?.id) {
      setSessions([]);
      return;
    }
    // Show the local cache instantly for a fast UI, then replace it with
    // the real, authoritative list from the backend once it arrives - the
    // cache alone is not the source of truth (confirmed 2026-09-01: a
    // user's real 88 sessions / 161 messages existed safely in Postgres
    // the whole time even when their local cache was empty/stale/wrong-
    // user's-data). This also means a fresh browser/device with no local
    // cache still correctly shows a user's real history.
    const saved = ls.get(`miraal_chat_sessions_${user.id}`, null);
    setSessions(Array.isArray(saved) ? saved : []);
    setIsLoaded(true);

    (async () => {
      try {
        const { sessions: serverSessions } = await apiClient.sessions.getSessions();
        if (Array.isArray(serverSessions)) {
          setSessions((prev) => {
            const prevById = new Map(prev.map((s) => [s.id, s]));
            return serverSessions.map((s: any) => ({
              id: s.id,
              title: s.title,
              messages: prevById.get(s.id)?.messages || [],
              updatedAt: s.updatedAt ? new Date(s.updatedAt).getTime() : Date.now(),
            }));
          });
        }
      } catch (err) {
        console.error('Could not refresh sessions from server, using local cache:', err);
      }
    })();
  }, [user?.id]);

  // Persist to localStorage - same per-user key as the load effect above.
  useEffect(() => {
    if (isLoaded && user?.id) {
      localStorage.setItem(`miraal_chat_sessions_${user.id}`, JSON.stringify(sessions));
    }
  }, [sessions, isLoaded, user?.id]);

  // Clear local-only (unsynced) sessions once the user actually logs in
  useEffect(() => {
    if (isAuthenticated && isLoaded) {
      const hasLocal = sessions.some((s) => s.id.startsWith('local_'));
      const activeIsLocal = activeSessionId?.startsWith('local_');
      if (hasLocal || activeIsLocal) {
        setSessions((prev) => prev.filter((s) => !s.id.startsWith('local_')));
        if (activeIsLocal) setActiveSessionId(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoaded]);

  const createNewChat = useCallback(async (): Promise<string> => {
    try {
      const serverSession = await apiClient.sessions.createSession('New Chat');
      const realId =
        serverSession?.session?.id ||
        serverSession?.id ||
        serverSession?.sessionId ||
        serverSession?.session_id ||
        serverSession?.data?.id ||
        serverSession?.data?.sessionId;

      if (realId) {
        const newSession: ChatSession = { id: realId, title: 'New Chat', messages: [], updatedAt: Date.now() };
        setSessions((prev) => [newSession, ...prev]);
        setActiveSessionId(realId);
        return realId;
      }
    } catch (err) {
      console.error('Backend session create failed:', err);
      toast.error('Could not start a new chat session — check your connection.');
    }

    // Fallback: local-only session (no streaming possible until a real
    // session exists — sendMessage() below refuses to run against these).
    const localId = `local_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const newSession: ChatSession = { id: localId, title: 'New Chat', messages: [], updatedAt: Date.now() };
    setSessions((prev) => [newSession, ...prev]);
    setActiveSessionId(localId);
    return localId;
  }, []);

  const selectChat = useCallback((id: string) => setActiveSessionId(id), []);

  // Real persistence, not just localStorage: whenever a session becomes
  // active (a click, a refresh that restores this id, direct navigation),
  // fetch its authoritative message history - including each message's
  // real attachments (chat_message_attachments, joined server-side) - from
  // Postgres and replace the local copy. Skipped for local-only sessions
  // (no backend counterpart yet) and while actively streaming into this
  // session (an in-flight response isn't in Postgres yet - refetching now
  // would erase it from the UI).
  useEffect(() => {
    if (!activeSessionId || activeSessionId.startsWith('local_')) return;
    if (isStreaming) return;
    let cancelled = false;
    (async () => {
      try {
        const server = await apiClient.sessions.getSession(activeSessionId);
        // Re-check at resolve time, not just at fetch-start: a message can
        // start sending (and streaming can begin) while this request was
        // still in flight. Applying a response that predates that would
        // silently erase the message that was just added locally.
        if (cancelled || !server?.messages || isStreamingRef.current) return;
        // Assistant content is persisted as the raw stream text, complete
        // with the <think>...</think> / <suggestions>...</suggestions>
        // tags the model appends - the live path only ever shows the
        // clean, split version because patchMessage() runs it through
        // these same two parsers on every token. Skipping that here left
        // a hydrated assistant message rendering as blank (react-markdown
        // silently drops unrecognized raw HTML-like tags with no
        // rehype-raw plugin configured) - a real bug, not a rare edge case.
        const hydrated: ChatMessage[] = server.messages.map((m: any) => {
          if (m.role !== 'assistant') {
            return {
              id: m.id,
              role: m.role,
              content: m.content,
              timestamp: m.createdAt ? new Date(m.createdAt).getTime() : Date.now(),
              model: m.model,
              attachments: Array.isArray(m.attachments) && m.attachments.length > 0 ? m.attachments : undefined,
            };
          }
          const { thinking, content: afterThinking } = splitThinking(m.content || '');
          const { content: visibleContent, suggestions } = splitSuggestions(afterThinking);
          return {
            id: m.id,
            role: m.role,
            content: visibleContent,
            thinking: thinking || undefined,
            timestamp: m.createdAt ? new Date(m.createdAt).getTime() : Date.now(),
            model: m.model,
            suggestions: suggestions.length > 0 ? suggestions : undefined,
          };
        });
        setSessions((prev) =>
          prev.map((s) => {
            if (s.id !== activeSessionId) return s;
            // Never let a hydration response regress message count - if the
            // local copy has since grown (a message sent during this
            // fetch), trust the local copy instead of an now-stale server
            // snapshot.
            if (hydrated.length < s.messages.length) return s;
            return { ...s, messages: hydrated };
          })
        );
      } catch (err) {
        // Non-fatal - the local/cached copy (if any) stays on screen.
        console.error('Could not load session history from server:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSessionId, isStreaming]);

  const deleteChat = useCallback(
    (id: string) => {
      setSessions((prev) => prev.filter((s) => s.id !== id));
      if (activeSessionId === id) setActiveSessionId(null);
    },
    [activeSessionId]
  );

  const renameChat = useCallback((id: string, title: string) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, title } : s)));
  }, []);

  const togglePin = useCallback((id: string) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, pinned: !s.pinned } : s)));
  }, []);

  const addMessage = useCallback((sessionId: string, message: ChatMessage) => {
    setSessions((prev) =>
      prev.map((s) => {
        if (s.id !== sessionId) return s;
        return {
          ...s,
          messages: [...s.messages, message],
          updatedAt: Date.now(),
          title: s.messages.length === 0 && message.role === 'user' ? message.content.slice(0, 40) : s.title,
        };
      })
    );
  }, []);

  const patchMessage = useCallback((sessionId: string, messageId: string, patch: Partial<ChatMessage>) => {
    setSessions((prev) =>
      prev.map((s) => {
        if (s.id !== sessionId) return s;
        return { ...s, messages: s.messages.map((m) => (m.id === messageId ? { ...m, ...patch } : m)) };
      })
    );
  }, []);

  const abortGeneration = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsStreaming(false);
    setIsLoading(false);
    setStreamingMessageId(null);
  }, []);

  const sendMessage = useCallback(
    async (sessionId: string, content: string, options?: SendOptions) => {
      const trimmed = content?.trim();
      const hasAttachments = !!options?.files && options.files.length > 0;
      // An attachment-only send (real file, no text) is valid - see
      // canSendMessage() in components/Chat/composerLogic.ts, which this
      // must agree with. Only reject when there is truly nothing to send.
      if (!trimmed && !hasAttachments) {
        toast.error('Message cannot be empty');
        return;
      }
      if (!sessionId) {
        toast.error('No active session');
        return;
      }

      if (sessionId.startsWith('local_')) {
        // No real backend session (e.g. session creation failed earlier) —
        // don't pretend to answer, tell the user plainly.
        addMessage(sessionId, {
          id: `msg_${Date.now() + 1}`,
          role: 'assistant',
          content: "This chat isn't connected to the backend, so I can't respond. Try starting a new chat once your connection is back.",
          timestamp: Date.now(),
        });
        return;
      }

      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const assistantId = `msg_${Date.now() + 1}`;
      addMessage(sessionId, {
        id: assistantId,
        role: 'assistant',
        content: '',
        thinking: '',
        thinkingOpen: false,
        timestamp: Date.now(),
        model: options?.model,
      });

      setIsLoading(true);
      setIsStreaming(true);
      setStreamingMessageId(assistantId);

      let raw = '';
      let thinkingStartedAt: number | null = null;
      let thinkingMs: number | undefined;
      let receivedAnyToken = false;
      let updateTimer: ReturnType<typeof setTimeout> | null = null;
      const UPDATE_INTERVAL_MS = 120; // ~8 commits/sec - genuine DOM churn reduction, not just capped-to-frame

      const applyUpdate = () => {
        updateTimer = null;
        const { thinking, content: afterThinking, isThinkingOpen } = splitThinking(raw);
        if (isThinkingOpen && thinkingStartedAt === null) thinkingStartedAt = Date.now();
        if (!isThinkingOpen && thinkingStartedAt !== null && thinkingMs === undefined) {
          thinkingMs = Date.now() - thinkingStartedAt;
        }
        const { content: visibleContent, suggestions } = splitSuggestions(afterThinking);
        patchMessage(sessionId, assistantId, {
          content: visibleContent,
          thinking,
          thinkingOpen: isThinkingOpen,
          thinkingMs,
          suggestions,
        });
      };

      // Throttles UI commits to roughly every 120ms (~8/sec) instead of
      // once per token or once per animation frame (up to 60/sec) - a fast
      // stream replacing a growing <pre> block's full text content that
      // often can itself look like visual churn/blur on some displays,
      // even though each individual update is cheap. This still reads as
      // smooth, continuous typing to the eye while cutting real DOM
      // updates by roughly 7x compared to the frame-capped version.
      const scheduleUpdate = () => {
        if (updateTimer) return;
        updateTimer = setTimeout(applyUpdate, UPDATE_INTERVAL_MS);
      };

      try {
        await apiClient.sessions.sendMessageStream(
          sessionId,
          trimmed,
          { model: options?.model, files: options?.files, signal: controller.signal },
          (token) => {
            receivedAnyToken = true;
            raw += token;
            scheduleUpdate();
          },
          (meta) => {
            if (meta?.modelUsed) patchMessage(sessionId, assistantId, { model: meta.modelUsed });
          },
          (status) => {
            patchMessage(sessionId, assistantId, { statusText: status });
          }
        );
        // Final synchronous flush - ensures the very last tokens are
        // applied even if a scheduled animation frame hadn't fired yet
        // when the stream itself finished (e.g. a very short response).
        applyUpdate();
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          // User hit stop — keep whatever partial content already streamed in.
        } else {
          console.error('Stream failed:', err);
          // `code` distinguishes known backend-classified failures from a
          // genuine connectivity/unknown error. Never surface the raw
          // backend error text here - it's safe-by-construction server-side
          // now, but the specific copy for each known case is still owned
          // by the frontend, not whatever string the backend happens to send.
          const isCapacity = err?.code === 'provider_capacity';
          // empty_response (see event_stream()'s empty-output guard in
          // main.py): the model spent its whole budget on hidden reasoning
          // and produced nothing visible. Tokens WERE streamed (the raw
          // <think> reasoning text), so receivedAnyToken is true here - but
          // there is still nothing real to show, unlike a merely-interrupted
          // stream where partial visible content should be kept.
          const isEmptyResponse = err?.code === 'empty_response';
          const userMessage = isCapacity
            ? 'AI provider is temporarily out of capacity, please try again shortly.'
            : isEmptyResponse
            ? "The AI couldn't generate a response that time - please try again."
            : "Sorry, I couldn't reach the backend. Please try again in a moment.";
          toast.error(userMessage);
          if (!receivedAnyToken || isEmptyResponse) {
            patchMessage(sessionId, assistantId, { content: userMessage, thinking: undefined, thinkingOpen: false });
          }
        }
      } finally {
        setIsLoading(false);
        setIsStreaming(false);
        setStreamingMessageId(null);
        abortControllerRef.current = null;
      }
    },
    [addMessage, patchMessage]
  );

  return (
    <ChatStoreContext.Provider
      value={{
        sessions,
        activeSession,
        activeSessionId,
        isLoaded,
        isLoading,
        isStreaming,
        streamingMessageId,
        createNewChat,
        selectChat,
        deleteChat,
        sendMessage,
        addMessage,
        abortGeneration,
        setSessions,
        setActiveSessionId,
        renameChat,
        togglePin,
      }}
    >
      {children}
    </ChatStoreContext.Provider>
  );
};

export const useChatStoreContext = () => {
  const ctx = useContext(ChatStoreContext);
  if (!ctx) throw new Error('useChatStoreContext must be used within ChatStoreProvider');
  return ctx;
};
